/**
 * @file index.tsx
 *
 * @fileoverview Renders a full width Hero search banner.
 */
import React from 'react';
import { HeroSearchBannerFullParagraph } from '~/jobiqo/types/types';
import Image from '~/jobiqo/components/Image';
import { JobSearchForm } from '~/components/Form/SearchInputForm';
import { RowButtons } from '../RowButtons';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { HeroRow } from '~/vcl/organisms/04-landingpage/02-hero-row';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { Main } from '~/vcl/atoms/06-layout/09-main';
import { getFrontendTheme } from '~/jobiqo/utils/getFrontend';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';
import styles from './heroSearchBannerFull.module.scss';

interface HeroSearchBannerFullProps {
  /**
   * The row to display.
   */
  row: HeroSearchBannerFullParagraph;
  /**
   * The index of this row. Used to detect if its the first row on the page.
   */
  index: number;
  /**
   * The max container size.
   */
  maxContainerSize?: 'small' | 'medium' | 'large' | 'xLarge';
}

export const HeroSearchBannerFull = ({
  row,
  maxContainerSize,
  index,
}: HeroSearchBannerFullProps) => {
  const frontendConfig = useFrontendConfig();
  const frontendTheme = getFrontendTheme(frontendConfig);
  const overlayTransparency = row.overlayTransparency ?? 100;
  // Convert configured transparency to CSS opacity for the dark overlay layer.
  const overlayOpacity = 1 - overlayTransparency / 100;

  return (
    <HeroRow
      boxPosition="background"
      // Set the image background to false if there is no h1.
      imageBackground={!!row.h1}
      image={
        <div
          className={styles.imageOverlay}
          style={{ '--overlay-opacity': overlayOpacity } as React.CSSProperties}
        >
          <Image
            src={row.image?.url}
            width={null}
            height={null}
            priority={index === 0}
            alt={row.image?.alt ?? ''}
            {...(row.image.title && { title: row.image.title })}
            {...(row.image.caption && { caption: row.image.caption })}
            quality={100}
          />
        </div>
      }
      boxContent={<JobSearchForm />}
    >
      <Spacing
        paddingTop={[5, row.spacing ?? 12]}
        paddingBottom={[0, row.spacing ?? 10]}
      >
        <Main {...(maxContainerSize ? { size: maxContainerSize } : null)}>
          <Text
            textAlign="center"
            tag="h1"
            fontSize={[6, 8]}
            lineHeight={[6, 7]}
          >
            {row.h1}
          </Text>
          <Spacing size="large" />
          <Text
            textAlign="center"
            color="gray6"
            fontSize={
              frontendTheme.LANDING_PAGES.HERO_SEARCH_BANNER_FULL
                .SUBTITLE_FONT_SIZE
            }
            lineHeight={
              frontendTheme.LANDING_PAGES.HERO_SEARCH_BANNER_FULL
                .SUBTITLE_LINE_HEIGHT
            }
          >
            {row.subText}
          </Text>

          <Spacing size="large" />
          <RowButtons
            button1={row.button1}
            button2={row.button2}
            buttonStyle={row.color as any}
          />
        </Main>
      </Spacing>
    </HeroRow>
  );
};
export default HeroSearchBannerFull;
